import React, { useEffect, useMemo, useRef, useState } from 'react';
import { preferencesApi } from '../../services/api';
import { calculateExpensePlan, normalizeExpensePlan, PLAN_CURRENCIES } from '../../utils/expensePlan';
import { formatCurrency } from '../../utils/formatCurrency';
import { CATEGORY_ORDER, getCategoryStyle } from './categoryStyles';

const PREFERENCE_KEY = 'expensePlan';
const STORAGE_KEY = 'saver-expense-plan';

const createItem = (id, name, amount, category, currency = 'GBP') => ({
  id, name, amount, category, currency,
});

export const DEFAULT_EXPENSE_PLAN = {
  active: false,
  salary: 3169.63,
  salaryCurrency: 'GBP',
  items: [
    createItem('food', 'Comida', 350, 'FOOD'),
    createItem('leisure', 'Lazer', 350, 'GO OUT'),
    createItem('t-usual', 'T-usual', 56.51, 'OTHERS'),
    createItem('rent', 'Alquiler', 976.03, 'BILLS'),
    createItem('fees', 'Tasas y contadora', 242.47, 'BILLS'),
    createItem('utilities', 'Luz, gas e internet', 154.11, 'BILLS'),
    createItem('spotify', 'Spotify', 4.79, 'BILLS'),
    createItem('vodafone', 'Vodafone', 17.12, 'BILLS'),
    createItem('icloud', 'iCloud', 8.99, 'BILLS'),
    createItem('amanda', 'Dinero Amanda', 110, 'AMANDA'),
    createItem('martin', 'Dinero Martin', 110, 'MARTIN'),
    createItem('mubi', 'Mubi', 1.83, 'BILLS'),
    createItem('medical', 'Seguro médico', 164.93, 'BILLS'),
    createItem('globoplay', 'Globoplay', 3.17, 'BILLS'),
    createItem('tidal', 'Tidal', 2.87, 'BILLS'),
    createItem('playstation', 'Playstation Plus', 5.76, 'BILLS'),
    createItem('youtube', 'YouTube Premium', 9.67, 'BILLS'),
    createItem('hbo', 'HBO Max', 4.30, 'BILLS'),
    createItem('chatgpt', 'ChatGPT', 20.53, 'BILLS'),
    createItem('claude', 'Claude', 16.03, 'BILLS'),
    createItem('hetzner', 'Hetzner', 15.67, 'BILLS'),
    createItem('car-insurance', 'Seguro Línea Directa', 12.02, 'BILLS'),
    createItem('computer-loan', 'Préstamo ordenador', 184.86, 'BILLS'),
    createItem('cofidis-loan', 'Préstamo Cofidis', 22.05, 'BILLS'),
    createItem('holidays', 'Vacaciones', 140, 'HOLIDAYS'),
  ],
};

const loadLocalPlan = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return normalizeExpensePlan(saved ? JSON.parse(saved) : null, DEFAULT_EXPENSE_PLAN);
  } catch (error) {
    return normalizeExpensePlan(null, DEFAULT_EXPENSE_PLAN);
  }
};

const formatOriginalCurrency = (amount, currency) => new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(amount) || 0);

const ExpensePlanV3 = ({ conversionRates = {}, onApplyBudgets }) => {
  const [plan, setPlan] = useState(loadLocalPlan);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const previousRates = useRef({ EUR: conversionRates.EUR, BRL: conversionRates.BRL });

  useEffect(() => {
    let active = true;
    preferencesApi.get(PREFERENCE_KEY).then(saved => {
      if (!active) return;
      if (saved) {
        const savedPlan = normalizeExpensePlan(saved, DEFAULT_EXPENSE_PLAN);
        setPlan(savedPlan);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(savedPlan));
      }
      setLoaded(true);
    }).catch(() => {
      if (active) setLoaded(true);
    });
    return () => { active = false; };
  }, []);

  const totals = useMemo(
    () => calculateExpensePlan(plan, CATEGORY_ORDER, conversionRates),
    [conversionRates, plan]
  );
  const draftTotals = useMemo(
    () => calculateExpensePlan(draft || plan, CATEGORY_ORDER, conversionRates),
    [conversionRates, draft, plan]
  );

  useEffect(() => {
    const ratesChanged = previousRates.current.EUR !== conversionRates.EUR
      || previousRates.current.BRL !== conversionRates.BRL;
    previousRates.current = { EUR: conversionRates.EUR, BRL: conversionRates.BRL };

    if (loaded && ratesChanged && plan.active) {
      onApplyBudgets(totals.categoryBudgets);
      setSaveMessage('Presupuestos recalculados con las nuevas tasas.');
    }
  }, [conversionRates.BRL, conversionRates.EUR, loaded, onApplyBudgets, plan.active, totals.categoryBudgets]);

  const startEditing = () => {
    setDraft(normalizeExpensePlan(plan, DEFAULT_EXPENSE_PLAN));
    setEditing(true);
    setSaveMessage('');
  };

  const updateDraftItem = (id, field, value) => {
    setDraft(previous => ({
      ...previous,
      items: previous.items.map(item => item.id === id ? { ...item, [field]: value } : item),
    }));
  };

  const addItem = () => {
    const id = `plan-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setDraft(previous => ({
      ...previous,
      items: [...previous.items, createItem(id, '', 0, 'BILLS')],
    }));
  };

  const removeItem = (id) => {
    setDraft(previous => ({ ...previous, items: previous.items.filter(item => item.id !== id) }));
  };

  const savePlan = async () => {
    const savedPlan = normalizeExpensePlan({ ...draft, active: true }, DEFAULT_EXPENSE_PLAN);
    const savedTotals = calculateExpensePlan(savedPlan, CATEGORY_ORDER, conversionRates);
    setPlan(savedPlan);
    setDraft(null);
    setEditing(false);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedPlan));
    await preferencesApi.set(PREFERENCE_KEY, savedPlan);
    onApplyBudgets(savedTotals.categoryBudgets);
    setSaveMessage('Planilla guardada y presupuestos actualizados.');
  };

  const cancelEditing = () => {
    setDraft(null);
    setEditing(false);
  };

  const balanceTone = totals.monthlyBalance >= 0 ? 'text-emerald-300' : 'text-red-300';

  return (
    <section className="mb-8 rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
      <div className="p-5 md:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold tracking-widest text-cyan-300 uppercase mb-1">Plan mensual</p>
            <h3 className="text-xl font-bold text-white">Planilla de gastos</h3>
            <p className="text-xs text-slate-400 mt-1">Tus gastos previstos se convierten a GBP y definen cada presupuesto.</p>
          </div>
          {editing ? (
            <div className="flex gap-2">
              <button type="button" onClick={cancelEditing} className="px-3 py-2 rounded-full text-xs font-semibold bg-white/5 text-slate-300 hover:bg-white/10 transition">Cancelar</button>
              <button type="button" onClick={savePlan} className="px-4 py-2 rounded-full text-xs font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/20 transition">Guardar y aplicar</button>
            </div>
          ) : (
            <button type="button" onClick={startEditing} className="px-4 py-2 rounded-full text-xs font-semibold bg-white/5 text-cyan-200 hover:bg-white/10 transition">Editar planilla</button>
          )}
        </div>

        {editing ? (
          <PlanEditor
            draft={draft}
            totals={draftTotals}
            conversionRates={conversionRates}
            onSalaryChange={(field, value) => setDraft(previous => ({ ...previous, [field]: value }))}
            onItemChange={updateDraftItem}
            onAddItem={addItem}
            onRemoveItem={removeItem}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
              <Metric label="Salario mensual" value={formatCurrency(totals.salary)} />
              <Metric label="Gastos previstos" value={formatCurrency(totals.monthlyExpenses)} />
              <Metric label="Sobra por mes" value={formatCurrency(totals.monthlyBalance)} tone={balanceTone} />
              <Metric label="Sobra en 12 meses" value={formatCurrency(totals.annualBalance)} tone={balanceTone} />
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {CATEGORY_ORDER.filter(category => totals.categoryBudgets[category] > 0).map(category => {
                const style = getCategoryStyle(category);
                return (
                  <span key={category} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/5 border border-white/5 text-[11px] text-slate-300">
                    <span aria-hidden="true">{style.emoji}</span>
                    {style.label} <strong className="text-white">{formatCurrency(totals.categoryBudgets[category])}</strong>
                  </span>
                );
              })}
            </div>
            <details className="group mt-4 pt-3 border-t border-white/10">
              <summary className="cursor-pointer list-none flex justify-between text-xs text-slate-400 hover:text-white transition">
                <span>Ver {plan.items.length} partidas mensuales</span>
                <span className="group-open:rotate-180 transition-transform" aria-hidden="true">⌄</span>
              </summary>
              <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-white/5 pr-1">
                {plan.items.map(item => {
                  const gbpValue = calculateExpensePlan({ salary: 0, items: [item] }, CATEGORY_ORDER, conversionRates).monthlyExpenses;
                  return (
                    <div key={item.id} className="flex items-center justify-between gap-3 py-2 text-xs">
                      <span className="min-w-0">
                        <span className="block text-slate-200 truncate">{item.name || 'Sin nombre'}</span>
                        <span className="text-[10px] text-slate-500">{getCategoryStyle(item.category).label}</span>
                      </span>
                      <span className="text-right shrink-0 tabular-nums">
                        <span className="block text-white">{formatOriginalCurrency(item.amount, item.currency)}</span>
                        {item.currency !== 'GBP' && <span className="text-[10px] text-cyan-300">≈ {formatCurrency(gbpValue)}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </details>
          </>
        )}

        {!plan.active && !editing && (
          <p className="mt-4 text-[11px] text-amber-300">Importé los valores de tu captura. Revísalos y pulsa “Guardar y aplicar” para sustituir los presupuestos actuales.</p>
        )}
        {saveMessage && !editing && <p className="mt-3 text-[11px] text-emerald-300">{saveMessage}</p>}
      </div>
    </section>
  );
};

const Metric = ({ label, value, tone = 'text-white' }) => (
  <div className="rounded-xl bg-slate-950/30 border border-white/5 p-3">
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 text-lg font-bold tabular-nums ${tone}`}>{value}</p>
  </div>
);

const PlanEditor = ({ draft, totals, conversionRates, onSalaryChange, onItemChange, onAddItem, onRemoveItem }) => (
  <div className="mt-5">
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3 p-4 rounded-xl bg-slate-950/30 border border-white/5">
      <label className="text-xs text-slate-300">
        <span className="block mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">Salario mensual</span>
        <input
          type="number"
          step="0.01"
          value={draft.salary}
          onChange={(event) => onSalaryChange('salary', event.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:ring-1 focus:ring-cyan-400"
        />
      </label>
      <label className="text-xs text-slate-300">
        <span className="block mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">Moneda</span>
        <select value={draft.salaryCurrency} onChange={(event) => onSalaryChange('salaryCurrency', event.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-white/10 text-white focus:outline-none focus:ring-1 focus:ring-cyan-400">
          {PLAN_CURRENCIES.map(currency => <option key={currency}>{currency}</option>)}
        </select>
      </label>
      {draft.salaryCurrency !== 'GBP' && (
        <p className="sm:col-span-2 text-[11px] text-cyan-300">Salario convertido: {formatCurrency(totals.salary)}</p>
      )}
    </div>

    <div className="mt-4 max-h-[430px] overflow-y-auto space-y-2 pr-1">
      {draft.items.map(item => {
        const itemInGBP = calculateExpensePlan({ salary: 0, items: [item] }, CATEGORY_ORDER, conversionRates).monthlyExpenses;
        return (
          <div key={item.id} className="grid grid-cols-[minmax(120px,1.5fr)_minmax(105px,1fr)_90px_76px_34px] gap-2 items-center min-w-[650px] p-2 rounded-lg bg-white/[0.03] border border-white/5">
            <input aria-label={`Nombre de ${item.name || 'nueva partida'}`} value={item.name} onChange={(event) => onItemChange(item.id, 'name', event.target.value)} placeholder="Nombre del gasto" className="px-2 py-1.5 rounded-md bg-white/10 border border-white/10 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-400" />
            <select aria-label={`Categoría de ${item.name || 'nueva partida'}`} value={item.category} onChange={(event) => onItemChange(item.id, 'category', event.target.value)} className="px-2 py-1.5 rounded-md bg-slate-800 border border-white/10 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-400">
              {CATEGORY_ORDER.map(category => <option key={category} value={category}>{getCategoryStyle(category).label}</option>)}
            </select>
            <input aria-label={`Importe de ${item.name || 'nueva partida'}`} type="number" step="0.01" value={item.amount} onChange={(event) => onItemChange(item.id, 'amount', event.target.value)} className="px-2 py-1.5 rounded-md bg-white/10 border border-white/10 text-xs text-right text-white tabular-nums focus:outline-none focus:ring-1 focus:ring-cyan-400" />
            <select aria-label={`Moneda de ${item.name || 'nueva partida'}`} value={item.currency} onChange={(event) => onItemChange(item.id, 'currency', event.target.value)} className="px-2 py-1.5 rounded-md bg-slate-800 border border-white/10 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-400">
              {PLAN_CURRENCIES.map(currency => <option key={currency}>{currency}</option>)}
            </select>
            <button type="button" onClick={() => onRemoveItem(item.id)} aria-label={`Eliminar ${item.name || 'partida'}`} className="w-8 h-8 rounded-md bg-red-500/10 text-red-300 hover:bg-red-500/20 transition">×</button>
            {item.currency !== 'GBP' && <span className="col-start-3 col-span-2 text-right text-[10px] text-cyan-300">≈ {formatCurrency(itemInGBP)}</span>}
          </div>
        );
      })}
    </div>

    <button type="button" onClick={onAddItem} className="mt-3 px-3 py-2 rounded-lg border border-dashed border-white/15 text-xs text-cyan-300 hover:bg-white/5 transition">+ Agregar gasto</button>

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
      <Metric label="Gastos en GBP" value={formatCurrency(totals.monthlyExpenses)} />
      <Metric label="Sobra por mes" value={formatCurrency(totals.monthlyBalance)} tone={totals.monthlyBalance >= 0 ? 'text-emerald-300' : 'text-red-300'} />
      <Metric label="Proyección 12 meses" value={formatCurrency(totals.annualBalance)} tone={totals.annualBalance >= 0 ? 'text-emerald-300' : 'text-red-300'} />
    </div>
  </div>
);

export default ExpensePlanV3;
